import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from '../../config';
import { useNavigate } from "react-router-dom";

const CDLoanPartnerwiseReport = () => {
  const [partners, setPartners] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState("");
  const [totalAmount, setTotalAmount] = useState(null);
  const navigate = useNavigate(); // Hook to navigate

  // Fetch partners on component mount
  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/cd-partners`);
        setPartners(response.data);
      } catch (error) {
        console.error("Error loading partner data:", error);
        alert("Failed to load partner data.");
      }
    };

    fetchPartners();
  }, []);

  // Fetch loan report data based on selected partner
  const fetchReportData = async (partner) => {
    if (!partner) {
      setTotalAmount(null);
      return;
    }

    try {
      const encodedPartner = encodeURIComponent(partner);
      const response = await axios.get(
        `${API_BASE_URL}/cd-loans-total-by-partner/${encodedPartner}`
      );
      setTotalAmount(Number(response.data?.total_amount || 0));
    } catch (error) {
      console.error("Error fetching report data:", error);
      alert("Failed to load report data.");
      setTotalAmount(null);
    }
  };

  // Handle partner selection change
  const handlePartnerChange = (e) => {
    const partner = e.target.value;
    setSelectedPartner(partner);
    fetchReportData(partner);
  };

  // Handle back button click
  const handleBackClick = () => {
    navigate(-1); // Go back to the previous page
  };

  return (
    <div className="partnerwise-report-container">
      {/* Inline CSS */}
      <style>
        {`
          /* General Styles */
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          body {
            font-family: 'Poppins', sans-serif;
            background: linear-gradient(135deg, #f3eae2, #e4d5c7);
            height: 100vh;
            overflow-y: auto;
            display: flex;
            justify-content: center;
            align-items: flex-start;
          }

          .partnerwise-report-container {
            width: 95%;
            max-width: 1100px; /* ✅ Increased container width */
            background-color: white;
            padding: 25px;
            border-radius: 12px;
            box-shadow: 0px 5px 15px rgba(0, 0, 0, 0.1);
            text-align: center;
            margin-top: 20px; /* ✅ Fixed margin at 20px */
          }

          /* Full-Width Back Button */
          .back-button {
            width: 100%;
            padding: 15px;
            background: #c0392b;
            color: white;
            font-size: 18px;
            font-weight: bold;
            border: none;
            cursor: pointer;
            border-radius: 8px;
            transition: all 0.3s ease-in-out;
            display: block;
            text-align: center;
            margin-bottom: 20px;
          }

          .back-button:hover {
            background: #a93226;
            transform: scale(1.02);
          }

          /* Page Heading */
          .partnerwise-report-container h2 {
            font-size: 28px;
            color: #2c3e50;
            margin-bottom: 20px;
            font-weight: bold;
          }

          /* Input Group */
          .input-group {
            margin-bottom: 20px;
            text-align: left;
            width: 50%;
            margin: auto;
          }

          .input-group label {
            font-weight: bold;
            display: block;
            margin-bottom: 8px;
          }

          .input-group select {
            width: 100%;
            padding: 12px;
            font-size: 16px;
            border: 1px solid #ccc;
            border-radius: 8px;
            background: #f9f9f9;
          }

          /* Report Table */
          .report-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
          }

          .report-table th, 
          .report-table td {
            text-align: left;
            padding: 14px;
            border: 1px solid #ddd;
            font-size: 16px;
          }

          .report-table th {
            background-color: #d8c3a5;
            color: #3b2c20;
            font-weight: bold;
            text-transform: uppercase;
          }

          .report-table tbody tr:nth-child(even) {
            background-color: #f9f9f9;
          }

          .report-table tbody tr:hover {
            background-color: #f3e5d8;
          }

          /* Responsive Design */
          @media (max-width: 768px) {
            .partnerwise-report-container {
              width: 98%;
            }

            .input-group {
              width: 80%;
            }

            .report-table th, .report-table td {
              font-size: 14px;
              padding: 10px;
            }

            .back-button {
              font-size: 16px;
              padding: 12px;
            }
          }
        `}
      </style>

      {/* Full-Width Back Button */}
      <button onClick={handleBackClick} className="back-button">
        Back
      </button>

      <h2>PARTNERWISE LOAN REPORT</h2>

      <div className="input-group">
        <label htmlFor="partner">Partner Name:</label>
        <select
          id="partner"
          value={selectedPartner}
          onChange={handlePartnerChange}
        >
          <option value="">Select Partner</option>
          {partners.map((partner, index) => (
            <option key={index} value={partner}>
              {partner}
            </option>
          ))}
        </select>
      </div>

      {selectedPartner && totalAmount !== null && (
        <table className="report-table">
          <thead>
            <tr>
              <th>Partner Name</th>
              <th>Total Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{selectedPartner}</td>
              <td>{totalAmount.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </div>
  );
};

export default CDLoanPartnerwiseReport;
